/**
 * Levelling: what a level costs, what it grants, and what a kill is worth.
 *
 * The curve is the one number that touches everything else — how deep a floor
 * has to be to matter, what a monster is worth, how long a character lasts
 * before the content runs out — so it is the one number here that is **not
 * invented**.
 *
 * ## The curve is D2's own table, row for row
 *
 * D2 does not compute experience from a formula; `Experience.txt` is 99
 * authored rows, and `EXPERIENCE_TABLE` below is those rows. 500 to reach level
 * 2, 47 116 709 to reach 50, **3 520 485 254 to reach 99**, which is also the
 * cap: a character stops earning there.
 *
 * Sources, cross-checked row by row against each other:
 *   - The Arreat Summit, "Basics: Experience Levels"
 *     (https://classic.battle.net/diablo2exp/basics/levels.shtml) — Blizzard's own.
 *   - Maxroll, "Experience Mechanics" (https://maxroll.gg/d2/resources/experience).
 *
 * The two agree on all 99 rows except level 13, where Maxroll's table renders
 * 112 275 against the Summit's 112 725. The Summit's is kept: the early table is
 * generated by a decreasing multiplier and level 13 is exactly `90 180 × 1.25`,
 * while 112 275 breaks the identity in both directions (level 14's 140 906 is
 * `112 725 × 1.25` floored, and is not `112 275 × 1.25` at all). A test pins
 * that identity so the transposition cannot creep back in.
 *
 * **Five stat points and one skill point per level** are D2's too, and are read
 * straight out of `CharStats.txt` — `StatPerLevel 5`, `SkillsPerLevel 1`, the
 * same for every class. Over 98 level-ups that is 490 stat points and 98 skill
 * points, which is the total every D2 guide prints. Quest rewards (D2 adds 4
 * skill and 5 stat points per difficulty) have nothing to hang off in this game
 * and are not modelled.
 *
 * ## What a monster is worth, and what falls out of `elites.ts`
 *
 * Two of the three numbers are D2's:
 *
 *   - a **champion pays 300%** of an ordinary monster's experience and a
 *     **unique, its minions and a Berserker champion pay 500%** (Maxroll,
 *     "Experience Mechanics" and "Elite Groups & Act Bosses");
 *   - an elite is also worth more because it *is* a higher-level monster —
 *     `+2` area levels for a champion, `+3` for a unique or a Berserker. That
 *     half costs this module nothing at all: `elites.ts` already puts the bonus
 *     into `ElitePack.level` when the pack is rolled, so an `ElitePack` handed
 *     to `monsterExperience` is worth more for both reasons and neither of them
 *     is stated twice. `ExperienceSource` is the narrow structural interface an
 *     `ElitePack` satisfies without either module importing the other, exactly
 *     as `Potion` satisfies `Placeable`.
 *
 * The third — what an *ordinary* monster of a given level is worth — is a
 * **placeholder**, in the sense `DEFAULT_MOVE_SPEED` and the drop rates are.
 * D2 authors it per monster type in `MonStats.txt`, and this game has no such
 * table (`TODO.md` #30). Rather than let each caller invent one, core states
 * one, beside the code that consumes it, and shows its working:
 *
 *   `worth = MONSTER_EXPERIENCE_COEFFICIENT × level²`
 *
 * Sixty monsters to a floor (`docs/GAME-DESIGN.md`) and `ilvl = depth`, so a
 * floor at depth *d* pays about `180 × d²` against a curve that asks
 * `E(d+1) − E(d)` to keep a character's level level with the floor:
 *
 * | depth | floor pays | the level costs | levels per floor |
 * |---|---|---|---|
 * | 1 | 180 | 500 | 0.36 |
 * | 5 | 4 500 | 6 300 | 0.71 |
 * | 10 | 18 000 | 14 429 | 1.25 |
 * | 15 | 40 500 | 44 033 | 0.92 |
 * | 20 | 72 000 | 134 378 | 0.54 |
 * | 30 | 162 000 | 829 810 | 0.20 |
 *
 * So a character starts a little behind the floor, draws level with it around
 * depth 10–15, and falls behind from about 18 — before elites, which pay three
 * to five times and are the reason a floor beats this table.
 *
 * **That plateau is real and it is not a bug in the coefficient.** D2's curve is
 * exponential and any polynomial worth loses to it eventually; D2's own answer
 * is three difficulties replaying the same acts at far higher monster levels,
 * plus a level 99 that almost nobody reaches. This game has one difficulty and
 * one axis — depth — so either the worth has to grow faster than a square, or
 * the curve has to be shorter than D2's. That is a balance decision, it is
 * Ronald's, and it is `TODO.md` #450.
 *
 * ## Who this module depends on, and which way the arrow points
 *
 * Two imports beyond the ledger and the stat block, and both are here because a
 * *level* is the thing that moves them:
 *
 *   - **`skills.ts`**, for `classSkillIds`. A character's skill ledger is opened
 *     over their class's own tree and over nothing else, so spending a point on
 *     another class's skill is impossible by construction rather than refused by
 *     a check somebody has to remember to write (`TODO.md` #456, #474b). The
 *     arrow is one-way at runtime: `skills.ts` names `CharacterProgress` as a
 *     *type* only, so its import of this module is erased and there is no cycle.
 *   - **`gold.ts`**, for `atLevel`. The purse's cap is `level × 10 000` and a
 *     level-up is the only thing that moves it, so the level-up transition is
 *     the one place that can re-seat it — `TODO.md` #454e. `awardKill` is that
 *     transition, and it is the reason `atLevel` finally has a caller.
 *
 * What a level-up *cannot* re-seat yet is the life and mana pools: both are
 * derived from attributes and gear by a character sheet nobody has assembled
 * (`TODO.md` #222), so `manaAtMaximum` — the mana equivalent of `atLevel` — has
 * no honest input to be given here. That gap is stated rather than papered over.
 *
 * ## What is deliberately not modelled
 *
 * D2 scales an award by the **gap between character level and monster level**
 * (three tiers of table: an `x/256` multiplier below level 25, `Experience ×
 * cLVL / mLVL` for a higher monster between 25 and 69, an `x/1024` multiplier
 * falling to 0.488% at 99) and **splits a party's award** by level share. Both
 * are stated at the source above and neither is implemented here — a level-90
 * character farming floor 1 is paid in full today. Declared rather than faked,
 * the way `elites.ts` declares an affix inert: `UNIMPLEMENTED_EXPERIENCE_RULES`
 * below, `TODO.md` #451.
 *
 * Everything here is exact integer arithmetic. The ceiling is 3.5 × 10⁹, four
 * orders of magnitude inside `Number.MAX_SAFE_INTEGER`, so nothing rounds.
 */
import { atLevel } from './gold.js';
import { createLedger, grantPoints } from './points.js';
import { classSkillIds } from './skills.js';
import { ATTRIBUTE_NAMES, createAttributes } from './stats.js';
/** The last level a character can reach. D2's own ceiling. */
export const MAX_CHARACTER_LEVEL = 99;
/** Stat points a level-up grants. D2's `CharStats.txt` `StatPerLevel`, 5 for every class. */
export const STAT_POINTS_PER_LEVEL = 5;
/** Skill points a level-up grants. D2's `CharStats.txt` `SkillsPerLevel`, 1 for every class. */
export const SKILL_POINTS_PER_LEVEL = 1;
/**
 * Cumulative experience needed to *reach* each level, index 0 being level 1.
 *
 * D2's `Experience.txt`, transcribed from the two sources in the module note.
 * Not a formula, not a fit to one — the rows themselves, so a player who knows
 * D2 recognises every number on the character sheet.
 */
export const EXPERIENCE_TABLE = [
    0, 500, 1_500, 3_750, 7_875, 14_175, 22_680, 32_886, 44_396, 57_715,
    72_144, 90_180, 112_725, 140_906, 176_132, 220_165, 275_207, 344_008, 430_010, 537_513,
    671_891, 839_864, 1_049_830, 1_312_287, 1_640_359, 2_050_449, 2_563_061, 3_203_826, 3_902_260, 4_663_553,
    5_493_363, 6_397_855, 7_383_752, 8_458_379, 9_629_723, 10_906_488, 12_298_162, 13_815_086, 15_468_534, 17_270_791,
    19_235_252, 21_376_515, 23_710_491, 26_254_525, 29_027_522, 32_050_088, 35_344_686, 38_935_798, 42_850_109, 47_116_709,
    51_767_302, 56_836_449, 62_361_819, 68_384_473, 74_949_165, 82_104_680, 89_904_191, 98_405_658, 107_672_256, 117_772_849,
    128_782_495, 140_783_010, 153_863_570, 168_121_381, 183_662_396, 200_602_101, 219_066_380, 239_192_444, 261_129_853, 285_041_630,
    311_105_466, 339_515_048, 370_481_492, 404_234_916, 441_026_148, 481_128_591, 524_840_254, 572_485_967, 624_419_793, 681_027_665,
    742_730_244, 809_986_056, 883_294_891, 963_201_521, 1_050_299_747, 1_145_236_814, 1_248_718_217, 1_361_512_946, 1_484_459_201, 1_618_470_619,
    1_764_543_065, 1_923_762_030, 2_097_310_703, 2_286_478_756, 2_492_671_933, 2_717_422_497, 2_962_400_612, 3_229_426_756, 3_520_485_254,
];
const assertLevel = (level) => {
    // Negated so NaN is refused with the out-of-range values.
    if (!(Number.isInteger(level) && level >= 1 && level <= MAX_CHARACTER_LEVEL)) {
        throw new RangeError(`A character level is a whole number in 1–${MAX_CHARACTER_LEVEL}, received ${level}`);
    }
};
const assertExperience = (what, value) => {
    if (!(Number.isInteger(value) && value >= 0)) {
        throw new RangeError(`${what} must be a whole non-negative number, received ${value}`);
    }
};
/** Total experience a character must have banked to stand at this level. */
export const experienceForLevel = (level) => {
    assertLevel(level);
    return EXPERIENCE_TABLE[level - 1] ?? 0;
};
/** What still stands between this level and the next. Zero at the ceiling — there is nothing left to buy. */
export const experienceToNextLevel = (level) => {
    assertLevel(level);
    return level === MAX_CHARACTER_LEVEL ? 0 : experienceForLevel(level + 1) - experienceForLevel(level);
};
/**
 * The level a total of experience buys.
 *
 * A linear walk rather than a binary search: 99 rows is nothing, and the plain
 * loop is the one both sides can read and agree about. Experience past the
 * ceiling reads as 99 rather than throwing — `gainExperience` is what refuses to
 * bank it, and a saved character that somehow carries more should still load.
 */
export const levelForExperience = (experience) => {
    assertExperience('An experience total', experience);
    let level = 1;
    while (level < MAX_CHARACTER_LEVEL && experience >= experienceForLevel(level + 1))
        level += 1;
    return level;
};
/**
 * D2's rank multipliers: "Champions award 300% of base XP"; "Uniques / Super
 * Uniques / Bosses award 500% of their base XP", and their minions with them.
 * Source: Maxroll, "Experience Mechanics".
 */
export const RANK_EXPERIENCE_PERCENT = {
    ordinary: 100,
    champion: 300,
    unique: 500,
};
/**
 * The one champion tier that breaks its rank's rate: "Berserker Champions award
 * 500% base XP" (Maxroll). Every other tier takes the rank's 300%, so this is a
 * table of exceptions rather than a second copy of the rank table — and the
 * Berserker's matching `+3` level bonus is already in `elites.ts`.
 */
export const CHAMPION_TIER_EXPERIENCE_PERCENT = {
    berserker: 500,
};
/**
 * What an ordinary monster is worth per level, squared. **A placeholder** —
 * D2 authors this per monster type in `MonStats.txt` and this game has no such
 * table (`TODO.md` #30). The pacing it produces is worked out in the module
 * note, and the shape is `TODO.md` #450.
 *
 * **Raised from 3 to 10 on 2026-08-13, on Ronald's call, after the pacing was
 * actually computed rather than assumed.** At 3, an ordinary depth-1 monster
 * was worth 3 and level 2 costs 500 — so reaching it took **167 kills**, and a
 * floor holds sixty. With no descent yet (M6), depth is always 1, so a player
 * could not reach level 2 without clearing roughly three rooms. No level 2
 * means no skill point, and no skill point means no cast: the placeholder was
 * quietly gating the whole skills feature behind half an hour of clicking.
 *
 * Ten is *more* faithful than three, not less. D2's own act-1 monsters give
 * roughly 5–15 to a character at level 1; three was the number with no source
 * behind it. It is still a placeholder and still wrong in shape — one
 * coefficient for every monster in the game is exactly what `MonStats.txt`
 * exists to avoid — and it stays flagged until that table is authored.
 */
export const MONSTER_EXPERIENCE_COEFFICIENT = 10;
/** How much of an ordinary monster's worth this source pays, in percent. */
export const experiencePercent = (source) => {
    const champion = source.champion;
    const tier = champion === null || champion === undefined ? undefined : CHAMPION_TIER_EXPERIENCE_PERCENT[champion.id];
    return tier ?? RANK_EXPERIENCE_PERCENT[source.rank];
};
/** What killing this monster pays, in whole experience. */
export const monsterExperience = (source) => {
    if (!(Number.isInteger(source.level) && source.level >= 1)) {
        throw new RangeError(`A monster level is a positive whole number, received ${source.level}`);
    }
    const base = MONSTER_EXPERIENCE_COEFFICIENT * source.level * source.level;
    return Math.floor((base * experiencePercent(source)) / 100);
};
/**
 * Whether a level and an experience total are the same fact, or `null` when
 * they are.
 *
 * The rule row 434 says nothing could be checked against: *"there is no
 * experience model, no per-level curve and no monotonicity check, so a write may
 * set any level between 1 and the column's ceiling and the gold cap moves with
 * it… it is exactly the check that becomes possible the day core owns
 * experience."* Core owns it, so here it is.
 *
 * A **returned result and not a throw**, unlike `createCharacterProgress`, and
 * the split is deliberate: constructing a progress out of two numbers that
 * disagree is a programmer error inside the sim, while a *save* that disagrees
 * is untrusted input arriving at a process boundary — and every other boundary
 * refusal in this codebase is a value the caller can turn into a 4xx. Where it
 * belongs is `apps/api`'s character write path, beside `isStorableGold`; core
 * states the rule and does not reach across the boundary to apply it.
 */
export const levelDisagreement = ({ level, experience }) => {
    // Negated so NaN is refused with the out-of-range values.
    if (!(Number.isInteger(level) && level >= 1 && level <= MAX_CHARACTER_LEVEL))
        return 'level-off-the-table';
    if (!(Number.isInteger(experience) && experience >= 0))
        return 'experience-not-whole';
    if (experience < experienceForLevel(level))
        return 'experience-below-level';
    // At the ceiling there is no next row to fall short of, so the level's own
    // total is both the floor and the roof: experience past 99 is not a level-99
    // character, it is a corrupt one.
    const roof = level === MAX_CHARACTER_LEVEL ? experienceForLevel(level) : experienceForLevel(level + 1) - 1;
    return experience > roof ? 'experience-above-level' : null;
};
/** Points a character standing at this level has been granted in total. */
const grantedBy = (level, perLevel) => perLevel * (level - 1);
/**
 * A character's progress, fresh or restored mid-run.
 *
 * An experience total that does not belong to the level throws rather than
 * being reconciled: the two are one fact stored twice, and a save that
 * disagrees with itself is a corrupt record, not a state the game can be in.
 * The rule it throws on is `levelDisagreement`'s, so there is one definition of
 * "these two agree" and the boundary check cannot drift from the constructor.
 */
export const createCharacterProgress = (input) => {
    const level = input.level ?? 1;
    assertLevel(level);
    const experience = input.experience ?? experienceForLevel(level);
    assertExperience('An experience total', experience);
    if (levelDisagreement({ level, experience }) !== null) {
        throw new RangeError(`A level ${level} character has banked ${experienceForLevel(level)}–${level === MAX_CHARACTER_LEVEL ? experienceForLevel(level) : experienceForLevel(level + 1) - 1} experience, received ${experience}`);
    }
    return {
        class: input.class,
        level,
        experience,
        attributePoints: createLedger(ATTRIBUTE_NAMES, grantedBy(level, STAT_POINTS_PER_LEVEL)),
        skillPoints: createLedger(classSkillIds(input.class, input.catalogue), grantedBy(level, SKILL_POINTS_PER_LEVEL)),
    };
};
/**
 * Banks experience and pays out every level it crossed.
 *
 * Several levels at once is an ordinary outcome — a first kill deep in the
 * dungeon, a boss — so the grant is `levels × per level` rather than a single
 * step, and the caller is told how many so it can show them.
 *
 * At the ceiling nothing is banked at all: `gained` comes back 0 and the same
 * progress object comes back with it, so a level-99 character costs a kill no
 * allocation and no wire delta.
 */
export const gainExperience = (progress, amount) => {
    assertExperience('An experience award', amount);
    const ceiling = experienceForLevel(MAX_CHARACTER_LEVEL);
    const gained = Math.min(amount, ceiling - progress.experience);
    if (gained === 0) {
        return { progress, gained: 0, levelsGained: 0, attributePointsGranted: 0, skillPointsGranted: 0 };
    }
    const experience = progress.experience + gained;
    const level = levelForExperience(experience);
    const levelsGained = level - progress.level;
    const attributePointsGranted = levelsGained * STAT_POINTS_PER_LEVEL;
    const skillPointsGranted = levelsGained * SKILL_POINTS_PER_LEVEL;
    return {
        progress: {
            ...progress,
            level,
            experience,
            attributePoints: grantPoints(progress.attributePoints, attributePointsGranted),
            skillPoints: grantPoints(progress.skillPoints, skillPointsGranted),
        },
        gained,
        levelsGained,
        attributePointsGranted,
        skillPointsGranted,
    };
};
/**
 * Pays out a monster's death.
 *
 * The one call `apps/game`'s floor tick needs when a body's pool empties
 * (`TODO.md` #454c): what the monster was worth, banked, with every level it
 * crossed granted and the gold cap moved to match. Composing it here rather
 * than in the room is what keeps the three from drifting apart — a room that
 * called `gainExperience` and forgot `atLevel` would leave a level-20 character
 * carrying a level-19 purse, and nothing would ever say so.
 *
 * The purse is re-seated whenever it disagrees with the level, not only on the
 * tick a level is gained. That makes it self-healing: a purse restored from a
 * save written before the last level-up catches up on the next kill instead of
 * carrying a stale ceiling for the rest of the run. A purse seated *above* the
 * character's level and holding more gold than the lower cap allows throws, and
 * should: character levels do not go down, so that pair can only be corrupt, and
 * `gold.ts` refuses to destroy gold to hide it.
 *
 * What it does **not** do is `UNIMPLEMENTED_EXPERIENCE_RULES`.
 */
export const awardKill = (earnings, source) => {
    const gain = gainExperience(earnings.progress, monsterExperience(source));
    const { level } = gain.progress;
    return {
        ...gain,
        purse: earnings.purse.level === level ? earnings.purse : atLevel(earnings.purse, level),
    };
};
/**
 * The two rules D2 applies to an award that this module does not, in prose —
 * the discipline `elites.ts` keeps for an affix it cannot pay, and `skills.ts`
 * for a skill D2 pays more for than core does.
 *
 * They are M6 and they are `TODO.md` #451, not oversights: the level gap needs
 * depth to be reachable before it can be tuned, and the party split needs a
 * party, which no room models yet. Both are stated at Maxroll, "Experience
 * Mechanics". Tests pin the *narrowed* behaviour, so whoever implements either
 * one finds a red suite and this list waiting to be shortened.
 */
export const UNIMPLEMENTED_EXPERIENCE_RULES = [
    'D2 scales an award by the gap between character level and monster level — an x/256 multiplier below level 25, `Experience x cLVL / mLVL` for a higher monster between 25 and 69, and an x/1024 multiplier falling to 0.488% at 99. Core pays a level-90 character in full for a floor-1 monster, so nothing discourages shallow farming.',
    'D2 splits a party award by each member\'s share of the party\'s total level. Core has no party: every character in a room collects the whole award independently, so four players are four times the experience.',
];
/**
 * The attributes a character actually has: their class base plus every point
 * they put in.
 *
 * The seam between the ledger and the stat block. `stats.ts` derives life,
 * mana, attack rating and defense from an `Attributes`, and this is what turns
 * a spent point into one — so a point of vitality is life and a point of energy
 * is mana without either module knowing a ledger exists. The *base* is the
 * caller's: per-class starting attributes are a table nobody has authored
 * (`TODO.md` #30).
 */
export const investedAttributes = (base, ledger) => createAttributes(Object.fromEntries(ATTRIBUTE_NAMES.map((name) => [name, base[name] + ledger.spent[name]])));
//# sourceMappingURL=progression.js.map